import type { Ref } from 'vue';
import { computed } from 'vue';

import type { FormGroup } from '@modules/forms/components/VForm/types';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';

import type { IFixedTrackerPaymentsComposableOptions, IFixedTrackerPaymentsForm, IFixedTrackerPaymentsFormSchema } from '../types';
import { TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_FIXED_PAYMENTS } from '../constants';

export interface IFixedTrackerPaymentsFormComposable {
  formSchema: Ref<IFixedTrackerPaymentsFormSchema>;
  formGroups: Ref<FormGroup<IFixedTrackerPaymentsForm>[]>;
}

export default function useFixedTrackerPaymentsForm(options: Ref<IFixedTrackerPaymentsComposableOptions>): IFixedTrackerPaymentsFormComposable {
  const { getTranslation } = useI18n();

  const formSchema = computed<IFixedTrackerPaymentsFormSchema>(() => {
    const schema: IFixedTrackerPaymentsFormSchema = {
      payment_size: {
        type: EFormInputTypes.NUMBER,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_FIXED_PAYMENTS, 'PAYMENT_SIZE_LABEL').value,
        value: options.value?.paymentSizeValue,
        required: true,
      },
      description: {
        type: EFormInputTypes.TEXTAREA,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_FIXED_PAYMENTS, 'DESCRIPTION_LABEL').value,
        value: options.value?.descriptionValue,
      },
    };

    if (options.value?.datePickerSelectionMode === 'single') {
      schema.date = {
        type: EFormInputTypes.DATE,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_FIXED_PAYMENTS, 'DATE_LABEL').value,
        value: options.value?.dateValue,
        required: true,
        view: 'month',
        dateFormat: 'mm.yy',
        selectionMode: 'single',
      };
    } else {
      schema.dates = {
        type: EFormInputTypes.DATE,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_FIXED_PAYMENTS, 'DATE_LABEL').value,
        value: options.value?.dateValue,
        required: true,
        view: 'month',
        dateFormat: 'mm.yy',
        selectionMode: 'multiple',
      };
    }

    if (!options.value?.hideSerialId) {
      schema.serial_id = {
        type: EFormInputTypes.NUMBER,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_FIXED_PAYMENTS, 'SERIAL_ID_LABEL').value,
        value: options.value?.serialIdValue,
        useGrouping: false,
        maxFractionDigits: 0,
        readonly: true,
      };
    }

    if (options.value.merchantNameValue) {
      schema.merchant_name = {
        type: EFormInputTypes.TEXT,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_FIXED_PAYMENTS, 'MERCHANT_NAME_LABEL').value,
        value: options.value.merchantNameValue,
        readonly: true,
      };
    }

    return schema;
  });

  const formGroups = computed<FormGroup<IFixedTrackerPaymentsForm>[]>(() => ([
    {
      items: options.value?.hideSerialId
        ? [
            'merchant_name',
            'payment_size',
            options.value?.datePickerSelectionMode === 'single' ? 'date' : 'dates',
            'description',
          ]
        : [
            'merchant_name',
            'serial_id',
            'payment_size',
            options.value?.datePickerSelectionMode === 'single' ? 'date' : 'dates',
            'description',
          ],
    },
  ]));

  return {
    formSchema,
    formGroups,
  };
}
